import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DateRangePickerComponent,
  OrganizationAnalyticsInfluencerTable,
  OrganizationInfluencerContentTypeByInfluencerChart,
  OrganizationInfluencerMessageTypeByInfluencerChart,
  OrganizationInfluencerMessageTypeByTimeChart,
  OrganizationInfluencerNewUsersByInfluencerChart,
  OrganizationInfluencerNewUsersConversionRateByInfluencerChart,
  OrganizationInfluencerNonSpendersByTimeChart,
  OrganizationInfluencerSpendersByTimeChart,
  OrganizationInfluencerTotalEarningsByTimeChart,
  SelectAlt,
  StatisticCard,
} from '../../../components';
import { ReactComponent as SafeIcon } from '../../../assets/icons/safe.svg';
import {
  fetchInfluencerEarningsByContentTypeByInfluencer,
  fetchInfluencerEarningsByContentTypeByTime,
  fetchInfluencerEarningsByMessageTypeByInfluencer,
  fetchInfluencerEarningsByMessageTypeByTime,
  fetchInfluencerTableData,
  fetchInfluencerUserInfluencersByInfluencer,
  fetchInfluencerUserInfluencersByTime,
} from '../../../store/slices/organizationAnalytics/asyncThunks';
import {
  selectDateRange,
  selectFetchInfluencerEarningsByContentTypeByInfluencerInfo,
  selectFetchInfluencerEarningsByContentTypeByTimeInfo,
  selectFetchInfluencerEarningsByMessageTypeByInfluencerInfo,
  selectFetchInfluencerEarningsByMessageTypeByTimeInfo,
  selectFetchInfluencerTableDataInfo,
  selectFetchInfluencerUserInfluencersByInfluencerInfo,
  selectFetchInfluencerUserInfluencersByTimeInfo,
  selectSelectedInfluencerIds,
  setDateRange,
  setSelectedInfluencerIds,
} from '../../../store/slices/organizationAnalytics/slice';
import {
  Content,
  FilterField,
  FiltersCard,
  FiltersGrid,
  FullWidthItem,
  ChartsGrid,
  StatsRow,
} from '../OrganizationAnalyticsPage/OrganizationAnalyticsPage.styles';
import { Text14Regular } from '../../../components/utils/Text/Text.styles';
import { fetchAllOrganizationInfluencers } from '../../../store/slices/chatter/asyncThunks';
import { selectInfluencerOptions } from '../../../store/slices/chatter/slice';
import useDebouncedValue from '../../../hooks/useDebouncedValue';

const OrganizationInfluencerAnalyticsPage = () => {
  const FILTER_DEBOUNCE_DELAY = 300;
  const dispatch = useDispatch();
  const dateRange = useSelector(selectDateRange);
  const influencerOptions = useSelector(selectInfluencerOptions);
  const selectedInfluencerIds = useSelector(selectSelectedInfluencerIds);
  const totalEarningsByTimeInfo = useSelector(selectFetchInfluencerEarningsByContentTypeByTimeInfo);
  const contentTypeByInfluencerInfo = useSelector(selectFetchInfluencerEarningsByContentTypeByInfluencerInfo);
  const messageTypeByTimeInfo = useSelector(selectFetchInfluencerEarningsByMessageTypeByTimeInfo);
  const messageTypeByInfluencerInfo = useSelector(selectFetchInfluencerEarningsByMessageTypeByInfluencerInfo);
  const userInfluencersByTimeInfo = useSelector(selectFetchInfluencerUserInfluencersByTimeInfo);
  const userInfluencersByInfluencerInfo = useSelector(selectFetchInfluencerUserInfluencersByInfluencerInfo);
  const influencerTableDataInfo = useSelector(selectFetchInfluencerTableDataInfo);

  const selectedInfluencers = useMemo(
    () => influencerOptions.filter(({ value }) => selectedInfluencerIds.includes(value)),
    [influencerOptions, selectedInfluencerIds],
  );
  const debouncedSelectedInfluencerIds = useDebouncedValue(selectedInfluencerIds, FILTER_DEBOUNCE_DELAY);

  const influencerIds = useMemo(() => [...debouncedSelectedInfluencerIds].sort(), [debouncedSelectedInfluencerIds]);

  const requestData = useMemo(
    () => ({
      from: dateRange[0],
      to: dateRange[1],
      ...(influencerIds.length && { influencerIds }),
    }),
    [dateRange, influencerIds],
  );

  const influencerLabelMap = useMemo(
    () => influencerOptions.reduce((acc, current) => ({ ...acc, [current.value]: current.label }), {}),
    [influencerOptions],
  );
  const sumArray = values => (values || []).reduce((sum, value) => sum + Number(value || 0), 0);
  const totalEarned = useMemo(
    () => sumArray(influencerTableDataInfo.data?.totalEarnings),
    [influencerTableDataInfo.data],
  );
  const totalMainFlowEarned = useMemo(
    () => sumArray(influencerTableDataInfo.data?.mainFlowEarnings),
    [influencerTableDataInfo.data],
  );
  const totalManualEarned = useMemo(
    () => sumArray(influencerTableDataInfo.data?.manualEarnings),
    [influencerTableDataInfo.data],
  );
  const totalMassDmEarned = useMemo(
    () => sumArray(influencerTableDataInfo.data?.massDmEarnings),
    [influencerTableDataInfo.data],
  );
  const loading =
    totalEarningsByTimeInfo.pending ||
    contentTypeByInfluencerInfo.pending ||
    messageTypeByTimeInfo.pending ||
    messageTypeByInfluencerInfo.pending ||
    userInfluencersByTimeInfo.pending ||
    userInfluencersByInfluencerInfo.pending ||
    influencerTableDataInfo.pending;

  useEffect(() => {
    dispatch(fetchAllOrganizationInfluencers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchInfluencerEarningsByContentTypeByTime(requestData));
    dispatch(fetchInfluencerEarningsByContentTypeByInfluencer(requestData));
    dispatch(fetchInfluencerEarningsByMessageTypeByTime(requestData));
    dispatch(fetchInfluencerEarningsByMessageTypeByInfluencer(requestData));
    dispatch(fetchInfluencerUserInfluencersByTime(requestData));
    dispatch(fetchInfluencerUserInfluencersByInfluencer(requestData));
    dispatch(fetchInfluencerTableData(requestData));
  }, [dispatch, requestData]);

  return (
    <Content>
      <FiltersCard>
        <FiltersGrid>
          <SelectAlt
            placeholder="All creators"
            label="Creators"
            options={influencerOptions}
            selectedOption={selectedInfluencers}
            handleChange={value =>
              dispatch(setSelectedInfluencerIds((value || []).map(influencer => influencer.value)))
            }
            disabled={loading}
            multiSelection
            isSearchable
          />
          <FilterField>
            <Text14Regular>Date Range</Text14Regular>
            <DateRangePickerComponent
              selectDateRangeFn={selectDateRange}
              setDateRangeAction={setDateRange}
              width={240}
              disabled={loading}
            />
          </FilterField>
        </FiltersGrid>
      </FiltersCard>

      <ChartsGrid>
        <StatsRow>
          <StatisticCard title="Total Earned" icon={<SafeIcon />} value={totalEarned} hideGross />
          <StatisticCard title="Total Main Flow Earned" icon={<SafeIcon />} value={totalMainFlowEarned} hideGross />
          <StatisticCard title="Total Manual Flow Earned" icon={<SafeIcon />} value={totalManualEarned} hideGross />
          <StatisticCard title="Total Mass DM Earned" icon={<SafeIcon />} value={totalMassDmEarned} hideGross />
        </StatsRow>
        <FullWidthItem>
          <OrganizationAnalyticsInfluencerTable
            data={influencerTableDataInfo.data}
            influencerLabelMap={influencerLabelMap}
            pending={influencerTableDataInfo.pending}
          />
        </FullWidthItem>
        <FullWidthItem>
          <OrganizationInfluencerTotalEarningsByTimeChart
            data={totalEarningsByTimeInfo.data}
            influencerLabelMap={influencerLabelMap}
            dateRange={dateRange}
            pending={totalEarningsByTimeInfo.pending}
          />
        </FullWidthItem>
        <OrganizationInfluencerContentTypeByInfluencerChart
          data={contentTypeByInfluencerInfo.data}
          influencerLabelMap={influencerLabelMap}
          pending={contentTypeByInfluencerInfo.pending}
        />
        <FullWidthItem>
          <OrganizationInfluencerMessageTypeByTimeChart
            data={messageTypeByTimeInfo.data}
            influencerLabelMap={influencerLabelMap}
            dateRange={dateRange}
            pending={messageTypeByTimeInfo.pending}
          />
        </FullWidthItem>
        <OrganizationInfluencerMessageTypeByInfluencerChart
          data={messageTypeByInfluencerInfo.data}
          influencerLabelMap={influencerLabelMap}
          pending={messageTypeByInfluencerInfo.pending}
        />
        <FullWidthItem>
          <OrganizationInfluencerSpendersByTimeChart
            data={userInfluencersByTimeInfo.data}
            influencerLabelMap={influencerLabelMap}
            dateRange={dateRange}
            pending={userInfluencersByTimeInfo.pending}
          />
        </FullWidthItem>
        <FullWidthItem>
          <OrganizationInfluencerNonSpendersByTimeChart
            data={userInfluencersByTimeInfo.data}
            influencerLabelMap={influencerLabelMap}
            dateRange={dateRange}
            pending={userInfluencersByTimeInfo.pending}
          />
        </FullWidthItem>
        <OrganizationInfluencerNewUsersByInfluencerChart
          data={userInfluencersByInfluencerInfo.data}
          influencerLabelMap={influencerLabelMap}
          pending={userInfluencersByInfluencerInfo.pending}
        />
        <OrganizationInfluencerNewUsersConversionRateByInfluencerChart
          data={userInfluencersByInfluencerInfo.data}
          influencerLabelMap={influencerLabelMap}
          pending={userInfluencersByInfluencerInfo.pending}
        />
      </ChartsGrid>
    </Content>
  );
};

export default OrganizationInfluencerAnalyticsPage;
